import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { NotificationService } from '../../../../shared/services/notification.service';
import { buildSearchPayload, mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BookingActivationGuardService } from '../../../services/booking-activation-guard.service';
import {
  LocationsActionTypes,
  LoadLocations,
  LoadLocationsSuccess,
  DeleteLocation,
  DeleteLocationSuccess,
  DeleteLocationFailure
} from '../../store/actions/locations.actions';
import { LocationsService } from '../../services/locations.service';
import { ConfirmDialogV2, ConfirmDialogDataV2 } from '../../../../shared/components/confirm-dialog.component';
import { createLocationTableConfig, TableRowAction } from './location-table.config';

@Component({
    selector: 'app-location-list',
    templateUrl: './location-list.component.html',
    styleUrls: ['./location-list.component.css'],
    standalone: false
})
export class LocationListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('table', {static: false}) table!: CustomTableComponent;
  tableConfig!: TableConfig;

  icon = 'home';
  title = 'Centros de trabajo';
  subTitle = 'Consulta y gestiona tus centros de trabajo';

  private destroy$ = new Subject<void>();
  private subscriptions: Subscription[] = [];
  private currentFilter: any = null;

  constructor(
    private bookingActivationGuard: BookingActivationGuardService,
    private router: Router,
    private store: Store<any>,
    private notify: NotificationService,
    private actions$: Actions,
    private locationsService: LocationsService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    // Inicializar la tabla inmediatamente para mostrar el loading
    this.tableConfig = createLocationTableConfig(
      {
        onCreate: () => this.newLocation(),
        onRowClick: (row) => console.log('Row click', row),
        onRowDblClick: (row) => this.viewLocationDetail(row),
        onRowExpand: (row) => console.log('Expandir detalle de', row),
        actionMap: {
          [TableRowAction.Access]: (row) => this.accessPublicUrl(row),
          [TableRowAction.AdminPanel]: (row) => this.openAdminPanel(row),
          [TableRowAction.Delete]: (row) => this.deleteLocation(row),
        },
        columns: [
          { key: 'name', label: 'Nombre', sortable: true, sortKey: 'Name' },
          { key: 'city', label: 'Ciudad', sortable: true, sortKey: 'City' },
          { key: 'publicUrl', label: 'URL Pública', sortable: false, onCellClick: this.accessPublicUrl.bind(this) },
          { key: 'statusName', label: 'Estado', sortable: true, sortKey: 'StatusName' },
        ],
      },
    );

    // Configurar subscripciones a las acciones
    this.setupSubscriptions();

    // Verificar la suscripción de booking
    this.bookingActivationGuard.verifyAndRedirectIfInactive()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result === true) {
          // La verificación pasó, la tabla ya está inicializada
        }
      });
  }

  private setupSubscriptions(): void {
    this.subscriptions.push(
      this.actions$.pipe(
        ofType<LoadLocationsSuccess>(LocationsActionTypes.LoadLocationsSuccess),
        map(action => action.payload)
      ).subscribe((res) => {
        if (this.table && this.table.tableState) {
          this.table.tableState.setData(res.items);
          this.table.tableState.setTotalItems(res.total);
          this.table.tableState.setLoading(false);
        }
      })
    );

    this.subscriptions.push(
      this.actions$.pipe(
        ofType(LocationsActionTypes.LoadLocationsFailure)
      ).subscribe((err: any) => {
        if (this.table && this.table.tableState) {
          this.table.tableState.setLoading(false);
        }
        this.notify.error(this.extractError(err) || 'No se pudo cargar los centros de trabajo');
      })
    );

    this.subscriptions.push(
      this.actions$.pipe(
        ofType<DeleteLocationSuccess>(LocationsActionTypes.DeleteLocationSuccess)
      ).subscribe((action) => {
        const count = action.payload.cancelledBookingsCount;
        this.notify.ok(
          count > 0
            ? `Centro de trabajo eliminado. ${count} reserva(s) activa(s) cancelada(s).`
            : 'Centro de trabajo eliminado.'
        );
        this.refreshTable();
      })
    );

    this.subscriptions.push(
      this.actions$.pipe(
        ofType(LocationsActionTypes.DeleteLocationFailure)
      ).subscribe((err: any) => {
        this.notify.error(this.extractError(err) || 'No se pudo eliminar el centro de trabajo');
      })
    );
  }

  newLocation(): void {
    this.router.navigate(['/booking/locations/settings/new']);
  }

  viewLocationDetail(row: any): void {
    this.router.navigate(['/booking/locations', row.merchantLocationId]);
  }

  accessPublicUrl(row: any): void {
    if (!row.publicUrl) {
      this.notify.error('No se pudo generar la URL pública para este centro de trabajo');
      return;
    }
    window.open(row.publicUrl, '_blank');
  }

  openAdminPanel(row: any): void {
    this.locationsService.getAdminPanelLink(row.merchantLocationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => window.open(res.url, '_blank'),
        error: () => this.notify.error('No se pudo conectar con el panel de administración de Booking')
      });
  }

  deleteLocation(row: any): void {
    if (!row || !row.merchantLocationId) {
      return;
    }

    this.locationsService.getDeleteImpact(row.merchantLocationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (!res || !res.isSuccess || !res.value) {
            this.notify.error('No se pudo consultar el impacto del borrado');
            return;
          }

          const impact = res.value;
          const activeCount = impact.activeBookingsCount;
          const name = impact.locationName || row.name || 'este centro de trabajo';

          const lines: string[] = [`¿Seguro que deseas eliminar "${name}"?`];
          if (activeCount > 0) {
            lines.push(
              `Esto cancelará ${activeCount} reserva${activeCount === 1 ? '' : 's'} activa${activeCount === 1 ? '' : 's'} asociada${activeCount === 1 ? '' : 's'}.`
            );
          }
          if (impact.isMainLocation) {
            lines.push('Este es el centro principal; perderá esa marca al eliminarse.');
          }
          lines.push('Esta acción no se puede deshacer.');

          const dialogRef = this.dialog.open(ConfirmDialogV2, {
            width: '460px',
            data: {
              title: 'Eliminar centro de trabajo',
              message: lines.join(' '),
              confirmText: 'Eliminar',
              cancelText: 'Cancelar',
              color: 'warn',
              icon: 'delete'
            } as ConfirmDialogDataV2
          });

          dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return;
            this.store.dispatch(new DeleteLocation({
              id: row.merchantLocationId,
              filter: this.currentFilter || { page: 1 }
            }));
          });
        },
        error: () => this.notify.error('No se pudo consultar el impacto del borrado')
      });
  }

  ngAfterViewInit(): void {
    if (!this.table || !this.table.tableState) {
      console.error('[ngAfterViewInit] Table not initialized - this should not happen');
      return;
    }

    this.table.tableState.state$.pipe(
      map(mapTableState),
      distinctUntilChanged(tableStateEquals),
      tap((state) => {
        localStorage.setItem('locations', JSON.stringify(state));
        const filter = { ...buildSearchPayload(state), page: state.pageIndex + 1 };
        this.currentFilter = filter;
        this.store.dispatch(new LoadLocations({ filter }));
        this.table.tableState.setLoading(true);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private refreshTable(): void {
    if (this.table && this.table.tableState) {
      this.table.tableState.setLoading(true);
    }
    const filter = this.currentFilter || { page: 1 };
    this.store.dispatch(new LoadLocations({ filter }));
  }

  private extractError(err: any): string | null {
    if (!err) return null;
    if (typeof err === 'string') return err;
    if (err.payload && err.payload.error) {
      const e = err.payload.error;
      if (typeof e === 'string') return e;
      if (e.message) return e.message;
      if (e.error && e.error.message) return e.error.message;
    }
    return null;
  }
}
